import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetMyOutletFoodItemsQuery } from "../slices/FoodItemSilce";
import {
  useCreateOuletOrderMutation,
  useEditOutletOrderMutation,
} from "../slices/OrdersSlice";
import { toast } from "react-toastify";
import {
  addOrderedItem,
  clearOrderedItems,
  removeOrderedItem,
  setOrderedItems,
  updateOrderedItem,
} from "../slices/orderedItemSlice";
import { printReceipt } from "../utils/printReceipt";
import { getCurrentDate } from "../utils/getCurrentDate";
import ReUsableModal from "../components/ReUsableModal";
import {
  Badge,
  Button,
  Col,
  Form,
  InputGroup,
  Row,
  Table,
} from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import LazyMemberImage from "../components/LazyMemberImage";
import { MdDeleteForever } from "react-icons/md";
import { setCredentials } from "../slices/authSlice";
import {
  customerMobileOf,
  formatAmount,
  makeFirstCharCapital,
} from "../utils/commonUtils";
import { GrEdit } from "react-icons/gr";
import useCustomersCache from "../useCustomersCache";
import OrderCard from "../components/OrderCard";
import { isValidGstNumber } from "../utils/gstValidation";

// Scrollable 0-1000 quantity picker. Opens with the current quantity
// highlighted and centered, lower numbers above it, higher numbers below.
// Positioned "fixed" so it floats above every other element and is never
// clipped by scrollable containers around it
const QuantityDropdown = ({ current, onSelect, onClose }) => {
  const listRef = useRef(null);
  const DROPDOWN_HEIGHT = 240;
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    // Anchor to the trigger element (the dropdown's DOM parent) and open
    // upwards when there is not enough space below the trigger.
    // Re-anchors on any scroll/resize so it stays stuck to its item
    const anchor = listRef.current?.parentElement;
    if (!anchor) return;

    const updatePosition = (e) => {
      // Scrolling inside the dropdown's own list does not move the anchor
      if (e && listRef.current?.contains(e.target)) return;

      const rect = anchor.getBoundingClientRect();
      const openUp =
        rect.bottom + DROPDOWN_HEIGHT > window.innerHeight &&
        rect.top > DROPDOWN_HEIGHT;
      setPosition({
        top: openUp ? rect.top - DROPDOWN_HEIGHT : rect.bottom,
        left: rect.left + rect.width / 2,
      });
    };

    updatePosition();
    // capture: true so scrolls inside any container (item grid, order
    // table) are caught, not just window scrolls
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  useEffect(() => {
    // Scroll only the dropdown's own list to center the current quantity.
    // scrollIntoView would also scroll the page/table containers around it
    const list = listRef.current;
    const selected = list?.querySelector('[data-current="true"]');
    if (list && selected) {
      list.scrollTop =
        selected.offsetTop - list.clientHeight / 2 + selected.clientHeight / 2;
    }
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (listRef.current && !listRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [onClose]);

  return (
    <div
      ref={listRef}
      style={{
        position: "fixed",
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        transform: "translateX(-50%)",
        visibility: position ? "visible" : "hidden",
        zIndex: 2147483647,
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        borderRadius: "8px",
        width: "4.5rem",
        maxHeight: `${DROPDOWN_HEIGHT}px`,
        overflowY: "auto",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.25)",
      }}
    >
      {Array.from({ length: 1001 }, (_, n) => (
        <div
          key={n}
          data-current={n === current}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(n);
            onClose();
          }}
          style={{
            padding: "0.15rem 0.4rem",
            textAlign: "center",
            cursor: "pointer",
            fontSize: "0.9rem",
            backgroundColor: n === current ? "#28a745" : "transparent",
            color: n === current ? "#fff" : "#000",
            fontWeight: n === current ? "bold" : "normal",
          }}
        >
          {n}
        </div>
      ))}
    </div>
  );
};

const NewSalesScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  // GST only enters price calculations here when the currently selected
  // outlet has a genuine 15-character GST number — matching the backend's
  // createOutletOrder/editOutletOrder rule exactly, so this preview never
  // diverges from what the server actually charges.
  const gstApplicable = isValidGstNumber(
    userInfo?.outletId?.find((ot) => ot._id === userInfo?.selectedOutletId)
      ?.outletGstNumber
  );
  const orderItems = useSelector((state) => state.orderedItem.items);
  const newOrders =
    userInfo?.newOrders?.filter(
      (order) =>
        String(order?.outletId?._id || order?.outletId) ===
        String(userInfo?.selectedOutletId)
    ) || [];
  const dispatch = useDispatch();

  // Fetches customers on page launch: full list on first ever visit,
  // only documents updated since lastCustomersRetrievedTime afterwards.
  // The cached list is available at userInfo.customersList in redux.
  const { customersList } = useCustomersCache();

  // Wholesale outlets usually deliver first and collect later, so
  // "Amount Collected" defaults to false there and to true everywhere else
  const isWholeSaleOutlet = Boolean(
    userInfo?.outletId?.find((ot) => ot._id === userInfo?.selectedOutletId)
      ?.wholeSaleOutlet
  );

  const [itemCode, setItemCode] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [itemName, setItemName] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [gst, setGst] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [matchingItems, setMatchingItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [grossTotal, setGrossTotal] = useState("0.00");
  const [availableQty, setAvailableQty] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [amountPaid, setAmountPaid] = useState(false);
  const [qtyDropdownItemCode, setQtyDropdownItemCode] = useState(null);
  const [paymentMode, setPaymentMode] = useState("Payment Mode");
  const itemCodeRef = useRef(null);
  const [isInvalidCode, setIsInvalidCode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [itemSearchText, setItemSearchText] = useState("");
  const [itemQuantities, setItemQuantities] = useState({});
  const quantityRef = useRef(null);
  const [orderDelivered, setOrderDelivered] = useState(false);
  const [isAmountCollected, setIsAmountCollected] = useState(
    !isWholeSaleOutlet
  );
  const [mobileRequiredError, setMobileRequiredError] = useState(false);
  const [paymentModeError, setPaymentModeError] = useState(false);
  // Wholesale outlets can back-date the invoice; sent as orderDate
  const [invoiceDate, setInvoiceDate] = useState(getCurrentDate());
  const [pendingOrders, setPendingOrders] = useState([]);
  const [pendingCounter, setPendingCounter] = useState(1);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [showSwitchModal, setShowSwitchModal] = useState(false);
  const [pendingToLoad, setPendingToLoad] = useState(null);
  const [editingQtyIndex, setEditingQtyIndex] = useState(null);
  const [editingSizeIndex, setEditingSizeIndex] = useState(null);
  const [activeOrderItem, setActiveOrderItem] = useState(null);
  // The existing order (from the history page) currently being edited.
  // null -> the screen behaves exactly as before (new order flow)
  const [editingOrder, setEditingOrder] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: foodItems,
    isLoading,
    refetch,
    error,
  } = useGetMyOutletFoodItemsQuery({ token: userInfo.token });

  const [placeOrder, { isLoading: placeOrderLoading }] =
    useCreateOuletOrderMutation();

  const [editOutletOrder, { isLoading: editOrderLoading }] =
    useEditOutletOrderMutation();

  useEffect(() => {
    if (!itemCode || !foodItems) {
      setMatchingItems([]);
      return;
    }

    const matches = foodItems.filter((item) => item.itemCode === itemCode);
    setMatchingItems(matches);

    if (matches.length === 0) {
      setIsInvalidCode(true);
      // toast.error("Invalid Item Code");
    } else if (matches.length === 1) {
      setIsInvalidCode(false);
      const selected = matches[0];
      fillItemDetails(selected);
    }
  }, [itemCode, foodItems]);

  useEffect(() => {
    if (foodItems && foodItems.length > 0) {
      const categories = getCategories();
      if (categories.length > 0) {
        setSelectedCategory(categories[0]);
      }
    }
  }, [foodItems]);

  useEffect(() => {
    if (selectedItemId && matchingItems.length > 1) {
      const selected = matchingItems.find(
        (item) => item._id === selectedItemId
      );
      if (selected) {
        fillItemDetails(selected);
      }
    }
  }, [selectedItemId, quantity]);

  useEffect(() => {
    const item = matchingItems.length === 1 ? matchingItems[0] : null;
    const size = item?.availableSizes?.find(
      (sz) => sz.sizeName === selectedSize
    );

    if (item && size) {
      const gstPercent = gstApplicable ? item?.gstTax || 0 : 0;
      const isSeparateGst = userInfo?.outletId.find(
        (ot) => ot._id === userInfo?.selectedOutletId
      )?.separateGst;

      const offer = size.itemPrice || 0;
      const discountPercent = size.discountPercent || 0;
      const discountAmount = size.discountAmount || 0;

      const priceAfterPercent = offer - (offer * discountPercent) / 100;
      const finalMrpPrice = priceAfterPercent - discountAmount;

      let finalSellingPrice,
        gstAmountPerUnit,
        totalGstAmount,
        totalPriceWithGst;
      const quantityNum = Number(quantity);

      if (isSeparateGst) {
        finalSellingPrice = finalMrpPrice;
        gstAmountPerUnit = (finalSellingPrice * gstPercent) / 100;
        totalGstAmount = gstAmountPerUnit * quantityNum;
        totalPriceWithGst = finalSellingPrice * quantityNum + totalGstAmount;
      } else {
        const basePrice = finalMrpPrice / (1 + gstPercent / 100);
        gstAmountPerUnit = finalMrpPrice - basePrice;
        totalGstAmount = gstAmountPerUnit * quantityNum;
        finalSellingPrice = basePrice;
        totalPriceWithGst = finalMrpPrice * quantityNum;
      }

      setSellingPrice(finalSellingPrice);
      setTotalPrice(totalPriceWithGst);

      const totalQtyNeeded = quantityNum * (size.consumptionQty || 1);
      setAvailableQty(
        totalQtyNeeded > item.itemQuantity ? item.itemQuantity : 10000
      );
    }
  }, [selectedSize, quantity]);

  useEffect(() => {
    const handleShortcut = (e) => {
      if (e.key === "Control" && orderItems.length > 0) {
        e.preventDefault();
        handleConfirmOrder();
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => {
      window.removeEventListener("keydown", handleShortcut);
    };
  }, [orderItems]);

  const fillItemDetails = (item) => {
    setItemName(item.itemName);
    setAvailableSizes(item.availableSizes || []);

    if (!item.availableSizes || item.availableSizes.length === 0) {
      setSelectedSize(null);
      setSellingPrice("");
      setTotalPrice("");
      setMobileNumber("");
      setAvailableQty(item.itemQuantity);
      return;
    }

    const existing = orderItems.find(
      (oi) => oi.itemCode === item.itemCode && oi.itemName === item.itemName
    );

    const sizeNameToUse =
      existing?.sizeName || item.availableSizes[0]?.sizeName;
    setSelectedSize(sizeNameToUse);
  };

  const calculatePrice = (item, size) => {
    const offer = size.itemPrice || 0;
    const discountPercent = size.discountPercent || 0;
    const discountAmount = size.discountAmount || 0;

    const priceAfterPercent = offer - (offer * discountPercent) / 100;
    const finalMrpPrice = priceAfterPercent - discountAmount;

    const gstPercent = gstApplicable ? item?.gstTax || 0 : 0;
    const quantityNum = Number(quantity);

    const isSeparateGst = userInfo?.outletId.find(
      (ot) => ot._id === userInfo?.selectedOutletId
    )?.separateGst;

    let selling, total;

    if (isSeparateGst) {
      selling = finalMrpPrice;
      total = finalMrpPrice * quantityNum;
    } else {
      const basePrice = finalMrpPrice / (1 + gstPercent / 100);
      selling = basePrice;
      total = finalMrpPrice * quantityNum;
    }

    setSellingPrice(selling);
    setTotalPrice(total);

    const totalQtyNeeded = quantityNum * (size.consumptionQty || 1);
    setAvailableQty(
      totalQtyNeeded > item.itemQuantity ? item.itemQuantity : 10000
    );
  };

  // Largest quantity of an item (in the given size) this order can take,
  // using the backend's rule: size.consumptionQty x quantity must not exceed
  // the item's stock. When editing an existing order the backend first gives
  // back the stock that order used, so it counts as available here too.
  // Infinity when the size uses no stock
  const getMaxOrderQuantity = (item, sizeName) => {
    const sizeOf = (name) =>
      item?.availableSizes?.find((sz) => sz.sizeName === name) ||
      item?.availableSizes?.[0];
    const perUnit = Number(sizeOf(sizeName)?.consumptionQty);
    if (!(perUnit > 0)) return Infinity;

    let stock = Number(item?.itemQuantity) || 0;
    (editingOrder?.orderDetails || []).forEach((line) => {
      const lineFoodId = String(line.foodItemId?._id || line.foodItemId || "");
      if (lineFoodId !== String(item?._id)) return;
      const lineConsumption = Number(line.consumptionQty);
      const linePerUnit =
        Number.isFinite(lineConsumption) && lineConsumption >= 0
          ? lineConsumption
          : Number(sizeOf(line.sizeName)?.consumptionQty) || 0;
      stock += linePerUnit * (Number(line.itemQuantity) || 0);
    });
    return Math.max(0, Math.floor(stock / perUnit + 1e-9));
  };

  // Warns and returns false when the quantity is more than the stock allows
  const isQuantityAvailable = (item, sizeName, qty) => {
    const maxQty = getMaxOrderQuantity(item, sizeName);
    if (Number(qty) <= maxQty) return true;
    const sizeLabel =
      sizeName && sizeName !== "Single Size" ? ` (${sizeName})` : "";
    toast.warn(
      maxQty > 0
        ? `Only ${maxQty} of ${item.itemName}${sizeLabel} available in stock`
        : `${item.itemName}${sizeLabel} is out of stock`,
      { toastId: `stock-${item._id}` }
    );
    return false;
  };

  const syncEverywhere = (item, qty, sizeName) => {
    const size = item.availableSizes?.find((sz) => sz.sizeName === sizeName);
    if (!size) return false;
    if (!isQuantityAvailable(item, size.sizeName, qty)) return false;

    const gstPercent = gstApplicable ? item?.gstTax || 0 : 0;

    const isSeparateGst = userInfo?.outletId.find(
      (ot) => ot._id === userInfo?.selectedOutletId
    )?.separateGst;

    const offer = size.itemPrice || 0;
    const discountPercent = size.discountPercent || 0;
    const discountAmount = size.discountAmount || 0;

    const priceAfterPercent = offer - (offer * discountPercent) / 100;

    const finalMrpPrice = priceAfterPercent - discountAmount;

    let finalSellingPrice = 0;
    let gstAmountPerUnit = 0;
    let totalGstAmount = 0;
    let totalPriceWithGst = 0;

    if (isSeparateGst) {
      finalSellingPrice = finalMrpPrice;

      gstAmountPerUnit = (finalSellingPrice * gstPercent) / 100;

      totalGstAmount = gstAmountPerUnit * qty;

      totalPriceWithGst = finalSellingPrice * qty + totalGstAmount;
    } else {
      finalSellingPrice = finalMrpPrice / (1 + gstPercent / 100);

      gstAmountPerUnit = finalMrpPrice - finalSellingPrice;

      totalGstAmount = gstAmountPerUnit * qty;

      totalPriceWithGst = finalMrpPrice * qty;
    }

    const updatedItem = {
      _id: item._id,
      itemCode: item.itemCode,
      itemName: item.itemName,
      quantity: qty,
      sizeName: size.sizeName,
      sellingPrice: finalSellingPrice,
      totalPrice: totalPriceWithGst,
      gstAmount: totalGstAmount,
      gstPercent,
      imageUploadedTime: item.imageUploadedTime,
      currentlySelling: item.currentlySelling,
      imageUploaded: item.imageUploaded,
    };

    setItemCode(updatedItem.itemCode);
    setItemName(updatedItem.itemName);
    setQuantity(updatedItem.quantity);
    setSelectedSize(updatedItem.sizeName);
    setSellingPrice(updatedItem.sellingPrice);
    setTotalPrice(updatedItem.totalPrice);

    setActiveOrderItem(updatedItem);

    const existingIndex = orderItems.findIndex(
      (oi) => oi.itemCode === updatedItem.itemCode
    );

    if (existingIndex !== -1) {
      dispatch(
        updateOrderedItem({
          index: existingIndex,
          updatedItem,
        })
      );
    } else {
      dispatch(addOrderedItem(updatedItem));
    }
    return true;
  };

  // Clears the item code entry row at the top. Clicking an item tile fills
  // that row too, so it must also be cleared once the order is placed
  const resetItemEntryFields = () => {
    setItemCode("");
    setQuantity(1);
    setItemName("");
    setSellingPrice("");
    setTotalPrice("");
    setSelectedItemId("");
    setSelectedSize(null);
    setAvailableSizes([]);
    setMatchingItems([]);
  };

  const handleAddItem = () => {
    if (itemCode && quantity && itemName && sellingPrice && totalPrice) {
      const selectedItem = foodItems.find(
        (item) =>
          item.itemCode === itemCode &&
          item.itemName === itemName &&
          (matchingItems.length === 1 || item._id === selectedItemId)
      );
      if (!selectedItem) {
        toast.error("Invalid Item Code.");
        return;
      }

      const isDuplicate = orderItems.some(
        (item) =>
          item.itemCode === selectedItem.itemCode &&
          item.itemName === selectedItem.itemName
      );
      if (isDuplicate) {
        toast.error("Item already added.");
        resetItemEntryFields();
        itemCodeRef.current?.focus();

        return;
      }

      const size = selectedItem.availableSizes?.find(
        (sz) => sz.sizeName === selectedSize
      );

      const gstPercent = gstApplicable ? selectedItem?.gstTax || 0 : 0;
      const isSeparateGst = userInfo?.outletId.find(
        (ot) => ot._id === userInfo?.selectedOutletId
      )?.separateGst;
      const quantityNum = Number(quantity);
      const offer = size?.itemPrice || 0;
      const discountPercent = size?.discountPercent || 0;
      const discountAmount = size?.discountAmount || 0;

      const priceAfterPercent = offer - (offer * discountPercent) / 100;
      const finalMrpPrice = priceAfterPercent - discountAmount;

      let finalSellingPrice = 0;
      let gstAmountPerUnit = 0;
      let totalGstAmount = 0;
      let totalPriceWithGst = 0;

      if (isSeparateGst) {
        finalSellingPrice = finalMrpPrice;
        gstAmountPerUnit = (finalSellingPrice * gstPercent) / 100;
        totalGstAmount = gstAmountPerUnit * quantityNum;
        totalPriceWithGst = finalSellingPrice * quantityNum + totalGstAmount;
      } else {
        finalSellingPrice = finalMrpPrice / (1 + gstPercent / 100);
        gstAmountPerUnit = finalMrpPrice - finalSellingPrice;
        totalGstAmount = gstAmountPerUnit * quantityNum;
        totalPriceWithGst = finalMrpPrice * quantityNum;
      }
      const newItem = {
        _id: selectedItem._id,
        imageUploadedTime: selectedItem.imageUploadedTime,
        currentlySelling: selectedItem.currentlySelling,
        imageUploaded: selectedItem.imageUploaded,
        itemCode,
        itemName,
        quantity,
        sizeName: size?.sizeName || null,
        sellingPrice,
        totalPrice: totalPriceWithGst,
        gstAmount: totalGstAmount,
        gstPercent: gstPercent,
      };

      if (
        !isQuantityAvailable(
          selectedItem,
          size?.sizeName || selectedSize,
          Number(quantity)
        )
      ) {
        return;
      }

      dispatch(addOrderedItem(newItem));

      setItemQuantities((prev) => {
        const updated = { ...prev };
        delete updated[itemCode];
        return updated;
      });

      resetItemEntryFields();
      itemCodeRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Customised prices of the customer whose mobile is currently entered
  // (wholesale outlets only, keyed by food item _id); {} when none apply
  const customPricesForCustomer = isWholeSaleOutlet
    ? customersList?.find((c) => customerMobileOf(c) === mobileNumber.trim())
        ?.customisedSellingPrice || {}
    : {};

  // Prices one order line: the customer's customised selling price (when
  // present) replaces the item's computed selling price (itemPrice minus
  // discounts); other lines keep the regular pricing already stored on
  // them. Used for the order table display, the confirm modal total and
  // the place/edit order payloads so they always agree
  const priceOrderLine = (item) => {
    const qty = Number(item?.quantity);
    const customValue = Number(
      customPricesForCustomer?.[String(item?._id)]?.sellingPrice
    );
    if (Number.isFinite(customValue) && customValue >= 0) {
      const gstPercent = gstApplicable ? Number(item?.gstPercent) || 0 : 0;
      const isSeparateGst = userInfo?.outletId.find(
        (ot) => ot._id === userInfo?.selectedOutletId
      )?.separateGst;
      let sellingPrice;
      let gstPerUnit;
      let unitTotal;
      if (isSeparateGst) {
        sellingPrice = customValue;
        gstPerUnit = (customValue * gstPercent) / 100;
        unitTotal = customValue + gstPerUnit;
      } else {
        sellingPrice = customValue / (1 + gstPercent / 100);
        gstPerUnit = customValue - sellingPrice;
        unitTotal = customValue;
      }
      return {
        sellingPrice: Number(sellingPrice.toFixed(2)),
        gstAmount: Number((gstPerUnit * qty).toFixed(2)),
        lineTotal: unitTotal * qty,
        isCustom: true,
      };
    }
    return {
      sellingPrice: parseFloat(item?.sellingPrice),
      gstAmount: parseFloat(item?.gstAmount),
      lineTotal: parseFloat(item.totalPrice),
      isCustom: false,
    };
  };

  const generateOrderDetailsAndGrossTotal = (orderItems) => {
    const orderDetails = orderItems.map((item) => {
      const priced = priceOrderLine(item);
      return {
        foodItemId: item?._id,
        itemQuantity: Number(item?.quantity),
        sizeName: item?.sizeName,
        gstAmount: priced.gstAmount,
        sellingPrice: priced.sellingPrice,
      };
    });

    const grossTotal = orderItems.reduce(
      (sum, item) => sum + priceOrderLine(item).lineTotal,
      0
    );

    return { orderDetails, grossTotal };
  };

  // Rebuilds one order-table line from the CURRENT food item pricing
  // (same math as handleAddItem), used when loading an existing order
  // for editing. The server recomputes prices the same way, so the
  // grossTotal check on editOutletOrder stays consistent
  const buildOrderItemFromFood = (foodItem, sizeName, quantityNum) => {
    const sizes = foodItem.availableSizes || [];
    const size =
      sizes.find(
        (sz) =>
          String(sz.sizeName || "").toLowerCase() ===
          String(sizeName || "").toLowerCase()
      ) || sizes[0];
    if (!size) return null;

    const gstPercent = gstApplicable ? foodItem?.gstTax || 0 : 0;
    const isSeparateGst = userInfo?.outletId.find(
      (ot) => ot._id === userInfo?.selectedOutletId
    )?.separateGst;
    const offer = size?.itemPrice || 0;
    const discountPercent = size?.discountPercent || 0;
    const discountAmount = size?.discountAmount || 0;
    const priceAfterPercent = offer - (offer * discountPercent) / 100;
    const finalMrpPrice = priceAfterPercent - discountAmount;

    let finalSellingPrice = 0;
    let gstAmountPerUnit = 0;
    if (isSeparateGst) {
      finalSellingPrice = finalMrpPrice;
      gstAmountPerUnit = (finalSellingPrice * gstPercent) / 100;
    } else {
      finalSellingPrice = finalMrpPrice / (1 + gstPercent / 100);
      gstAmountPerUnit = finalMrpPrice - finalSellingPrice;
    }
    const totalGstAmount = gstAmountPerUnit * quantityNum;
    const totalPriceWithGst = isSeparateGst
      ? finalSellingPrice * quantityNum + totalGstAmount
      : finalMrpPrice * quantityNum;

    return {
      _id: foodItem._id,
      imageUploadedTime: foodItem.imageUploadedTime,
      currentlySelling: foodItem.currentlySelling,
      imageUploaded: foodItem.imageUploaded,
      itemCode: foodItem.itemCode,
      itemName: foodItem.itemName,
      quantity: quantityNum,
      sizeName: size?.sizeName || null,
      sellingPrice: finalSellingPrice,
      totalPrice: totalPriceWithGst,
      gstAmount: totalGstAmount,
      gstPercent,
    };
  };

  // When the history page navigates here with an order to edit, load its
  // items into the order table once the food items are available
  useEffect(() => {
    const orderToEdit = location.state?.editOrder;
    if (!orderToEdit || !foodItems?.length || editingOrder) return;

    const items = [];
    const missing = [];
    (orderToEdit.orderDetails || []).forEach((od) => {
      const foodItem = foodItems.find(
        (fi) => String(fi._id) === String(od.foodItemId?._id || od.foodItemId)
      );
      if (!foodItem) {
        missing.push(od.itemName);
        return;
      }
      const built = buildOrderItemFromFood(
        foodItem,
        od.sizeName,
        Number(od.itemQuantity) || 1
      );
      if (built) items.push(built);
      else missing.push(od.itemName);
    });

    if (missing.length) {
      toast.warn(`Skipped items no longer available: ${missing.join(", ")}`);
    }
    if (!items.length) {
      toast.error("None of this order's items are available to edit");
      navigate("/orderHistory");
      return;
    }

    dispatch(setOrderedItems(items));
    setEditingOrder(orderToEdit);
    setMobileNumber(
      orderToEdit.mobileNumber ? String(orderToEdit.mobileNumber) : ""
    );
    setOrderDelivered(orderToEdit.orderStatus === "Delivered");
    setIsAmountCollected(Number(orderToEdit.dueAmount || 0) === 0);
    if (orderToEdit.paymentMode) setPaymentMode(orderToEdit.paymentMode);
    setInvoiceDate(
      (orderToEdit.orderDate || orderToEdit.createdAt || "").split("T")[0] ||
        getCurrentDate()
    );
    // Clear the router state so a page refresh does not re-load the order
    window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, foodItems]);

  // Leaves edit mode: clears the order table and every edit-related
  // field, then returns to the history page
  const handleCancelEdit = () => {
    dispatch(clearOrderedItems());
    setItemQuantities({});
    setSelectedSize(null);
    setActiveOrderItem(null);
    setMobileNumber("");
    setEditingOrder(null);
    setIsAmountCollected(!isWholeSaleOutlet);
    setOrderDelivered(false);
    setPaymentMode("Payment Mode");
    setInvoiceDate(getCurrentDate());
    setShowModal(false);
    navigate("/orderHistory");
  };

  const totalOrderAmount = orderItems.reduce(
    (sum, item) => sum + priceOrderLine(item).lineTotal,
    0
  );
  const handleConfirmOrder = () => {
    if (!validateOrderInputs()) return;
    const { grossTotal } = generateOrderDetailsAndGrossTotal(
      orderItems,
      foodItems,
      userInfo
    );
    setGrossTotal(grossTotal);
    setShowModal(true);
  };

  // Customer search on the cached customers list in redux,
  // matches by customerName or mobile number (_id)
  const customerSuggestions = customerSearchText.trim()
    ? customersList
        .filter(
          (customer) =>
            customer?.customerName
              ?.toLowerCase()
              .includes(customerSearchText.trim().toLowerCase()) ||
            customerMobileOf(customer).includes(customerSearchText.trim())
        )
        .slice(0, 8)
    : [];

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setMobileNumber(customerMobileOf(customer));
    setCustomerSearchText("");
    setAmountPaid(false);
  };

  // If the mobile number is cleared or manually changed to another number,
  // the selected customer (and its postPaid default) no longer applies
  useEffect(() => {
    if (selectedCustomer && mobileNumber !== customerMobileOf(selectedCustomer)) {
      setSelectedCustomer(null);
      setAmountPaid(false);
    }
  }, [mobileNumber, selectedCustomer]);

  // Re-apply the outlet-based default if the selected outlet changes
  useEffect(() => {
    setIsAmountCollected(!isWholeSaleOutlet);
  }, [isWholeSaleOutlet]);

  // Clear the mobile-required warning once the user fixes it
  useEffect(() => {
    if (isAmountCollected || mobileNumber.trim().length >= 10) {
      setMobileRequiredError(false);
    }
  }, [mobileNumber, isAmountCollected]);

  // Clear the payment-mode warning once the user fixes it
  useEffect(() => {
    if (!isAmountCollected || paymentMode !== "Payment Mode") {
      setPaymentModeError(false);
    }
  }, [paymentMode, isAmountCollected]);

  // When amount is not collected the order must be tied to a customer, so a
  // valid mobile number is mandatory. When amount IS collected, the payment
  // mode it was collected through is mandatory
  const validateOrderInputs = () => {
    if (!isAmountCollected && mobileNumber.trim().length < 10) {
      setMobileRequiredError(true);
      toast.error("Customer mobile is required when amount is not collected");
      return false;
    }
    if (isAmountCollected && paymentMode === "Payment Mode") {
      setPaymentModeError(true);
      toast.error("Please select a payment mode when amount is collected");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateOrderInputs()) {
      setShowModal(false);
      return;
    }
    try {
      const { orderDetails, grossTotal } = generateOrderDetailsAndGrossTotal(
        orderItems,
        foodItems,
        userInfo
      );

      // Editing an existing order (opened via the history page's Edit
      // button) updates it through editOutletOrder instead of creating
      // a new order, then returns to the history page
      if (editingOrder) {
        const editPayload = {
          order_id: editingOrder._id,
          outletId:
            editingOrder.outletId?._id ||
            editingOrder.outletId ||
            foodItems[0]?.outletId?._id,
          orderDetails,
          grossTotal: grossTotal.toFixed(2),
          mobileNumber: mobileNumber.trim(),
          orderStatus: orderDelivered ? "Delivered" : "Confirmed",
          isAmountCollected,
          paymentMode: paymentMode !== "Payment Mode" ? paymentMode : undefined,
          orderDate: isWholeSaleOutlet ? invoiceDate : undefined,
          token: userInfo?.token,
        };

        await editOutletOrder(editPayload).unwrap();
        toast.success("Order Updated Successfully");
        dispatch(clearOrderedItems());
        setItemQuantities({});
        setSelectedSize(null);
        setActiveOrderItem(null);
        setMobileNumber("");
        setShowModal(false);
        setEditingOrder(null);
        setIsAmountCollected(!isWholeSaleOutlet);
        setOrderDelivered(false);
        setPaymentMode("Payment Mode");
        setInvoiceDate(getCurrentDate());
        refetch();
        navigate("/orderHistory");
        return;
      }

      // Postpaid customer -> postPaid true by default; the "Amount Paid"
      // toggle overrides it to false. Non-postpaid customers -> false
      const orderPayload = {
        outletId: foodItems[0]?.outletId?._id,
        orderDetails,
        grossTotal: grossTotal.toFixed(2),
        mobileNumber: mobileNumber.trim(),
        postPaid: Boolean(selectedCustomer?.postPaidCustomer && !amountPaid),
        token: userInfo?.token,
        orderStatus: orderDelivered ? "Delivered" : "Confirmed",
        isAmountCollected,
        paymentMode: paymentMode !== "Payment Mode" ? paymentMode : undefined,
        orderDate: isWholeSaleOutlet ? invoiceDate : undefined,
      };

      const orderResponse = await placeOrder(orderPayload).unwrap();

      if (orderResponse) {
        toast.success("Order Placed Successfully");
        let updatedPending = pendingOrders;
        if (editingOrderId) {
          updatedPending = pendingOrders.filter(
            (order) => order.id !== editingOrderId
          );

          setPendingOrders(updatedPending);
          dispatch(
            setCredentials({
              ...userInfo,
              pendingOrders: updatedPending,
              newOrders: [orderResponse],
            })
          );
        } else {
          dispatch(
            setCredentials({
              ...userInfo,
              pendingOrders: updatedPending,
              newOrders: [orderResponse],
            })
          );
        }
        dispatch(clearOrderedItems());
        setItemQuantities({});
        resetItemEntryFields();
        setActiveOrderItem(null);
        setMobileNumber("");
        setShowModal(false);
        setEditingOrderId(null);
        setIsAmountCollected(!isWholeSaleOutlet);
        setOrderDelivered(false);
        setPaymentMode("Payment Mode");
        setInvoiceDate(getCurrentDate());
        try {
          await printReceipt(orderResponse, userInfo);
        } catch (err) {
          console.error("Print error:", err.message);
          // toast.error("Print error:", err.message);
        }
        refetch();
      } else {
        toast.error("Order Placement Failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.data?.message || "Something went wrong while placing the order."
      );
    }
  };
  const handleCancel = () => {
    setShowModal(false);
  };

  const handleDeleteItem = (index) => {
    if (orderItems.length === 1 && editingOrderId) {
      const updated = pendingOrders.filter(
        (order) => order.id !== editingOrderId
      );

      setPendingOrders(updated);
      setPendingCounter(updated.length + 1);

      dispatch(
        setCredentials({
          ...userInfo,
          pendingOrders: updated,
        })
      );

      dispatch(clearOrderedItems());
      setMobileNumber("");
      setEditingOrderId(null);

      return;
    }
    dispatch(removeOrderedItem(index));
  };

  const getCategories = () => {
    if (!foodItems) return [];
    const categorySet = new Set();
    foodItems.forEach((item) => {
      if (item.itemCategory) categorySet.add(item.itemCategory);
    });
    return Array.from(categorySet);
  };

  const getItemsByCategory = (category) => {
    return foodItems?.filter((item) => item.itemCategory === category);
  };

  // While a search is active the items list shows matches from every
  // category (by name or item code) instead of the selected category
  const itemSearchTerm = itemSearchText.trim().toLowerCase();
  const isItemSearchActive = itemSearchTerm.length > 0;
  const itemsToShow = isItemSearchActive
    ? (foodItems || []).filter(
        (item) =>
          item.itemName?.toLowerCase().includes(itemSearchTerm) ||
          String(item.itemCode ?? "")
            .toLowerCase()
            .includes(itemSearchTerm)
      )
    : getItemsByCategory(selectedCategory);

  useEffect(() => {
    const storedPending = userInfo?.pendingOrders || [];

    const outletPendingOrders = storedPending.filter(
      (order) => String(order.outletId) === String(userInfo?.selectedOutletId)
    );
    setPendingOrders(outletPendingOrders);

    if (storedPending.length > 0) {
      const maxSequence = Math.max(
        ...outletPendingOrders.map((o) => o.sequence || 0)
      );
      setPendingCounter(maxSequence + 1);
    } else {
      setPendingCounter(1);
    }
  }, [userInfo?.pendingOrders, userInfo?.selectedOutletId]);

  const PENDING_EXPIRY_MINUTES = 20;

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();

      const updated = pendingOrders.filter((order) => {
        const createdTime = new Date(order.createdAt);
        const diffMinutes = (now - createdTime) / (1000 * 60);

        return diffMinutes < PENDING_EXPIRY_MINUTES;
      });

      if (updated.length !== pendingOrders.length) {
        setPendingOrders(updated);
        setPendingCounter(updated.length + 1);

        dispatch(
          setCredentials({
            ...userInfo,
            pendingOrders: updated,
          })
        );
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [pendingOrders, userInfo]);

  const buildPendingOrder = () => ({
    id: Date.now().toString(),
    sequence: pendingCounter,
    outletId: userInfo?.selectedOutletId,
    items: JSON.parse(JSON.stringify(orderItems)),
    mobileNumber,
    createdAt: new Date(),
  });

  const handleMoveToPending = () => {
    if (orderItems.length === 0) return;

    let updatedPending;

    if (editingOrderId) {
      updatedPending = pendingOrders.map((order) =>
        order.id === editingOrderId
          ? {
              ...order,
              items: JSON.parse(JSON.stringify(orderItems)),
              mobileNumber,
              createdAt: new Date(),
            }
          : order
      );
    } else {
      updatedPending = [...pendingOrders, buildPendingOrder()];
    }

    setPendingOrders(updatedPending);

    setPendingCounter((prev) => prev + 1);

    dispatch(
      setCredentials({
        ...userInfo,
        pendingOrders: updatedPending,
      })
    );

    dispatch(clearOrderedItems());
    setMobileNumber("");
    setEditingOrderId(null);
  };

  const handleLoadPending = (order) => {
    if (editingOrder) {
      toast.error(
        "You are editing an existing order. Cancel the edit before loading a pending order"
      );
      return;
    }
    if (orderItems.length > 0 && order.id !== editingOrderId) {
      setShowSwitchModal(true);
      setPendingToLoad(order);
      return;
    }

    dispatch(setOrderedItems(order.items));
    setMobileNumber(order.mobileNumber || "");

    setEditingOrderId(order.id);
  };

  const updatePendingOrders = (existingPending) => {
    let updated = [...existingPending];

    if (orderItems.length > 0) {
      if (editingOrderId) {
        updated = updated.map((order) =>
          order.id === editingOrderId
            ? {
                ...order,
                items: JSON.parse(JSON.stringify(orderItems)),
                mobileNumber,
                createdAt: new Date(),
              }
            : order
        );
      } else {
        updated.push(buildPendingOrder());
        setPendingCounter((prev) => prev + 1);
      }
    }

    return updated;
  };

  const syncItemEverywhere = (itemCode, newQty, newSize = null) => {
    const selectedItem = foodItems.find((fi) => fi.itemCode === itemCode);

    if (!selectedItem) return;

    const existingIndex = orderItems.findIndex(
      (oi) => oi.itemCode === itemCode
    );

    if (existingIndex === -1) return;

    const existingItem = orderItems[existingIndex];

    const sizeName = newSize || existingItem.sizeName;

    const size = selectedItem.availableSizes?.find(
      (sz) => sz.sizeName === sizeName
    );

    if (!size) return;
    if (!isQuantityAvailable(selectedItem, size.sizeName, newQty)) return;

    const gstPercent = gstApplicable ? selectedItem?.gstTax || 0 : 0;

    const isSeparateGst = userInfo?.outletId.find(
      (ot) => ot._id === userInfo?.selectedOutletId
    )?.separateGst;

    const offer = size.itemPrice || 0;

    const discountPercent = size.discountPercent || 0;

    const discountAmount = size.discountAmount || 0;

    const priceAfterPercent = offer - (offer * discountPercent) / 100;

    const finalMrpPrice = priceAfterPercent - discountAmount;

    let finalSellingPrice = 0;
    let gstAmountPerUnit = 0;
    let totalGstAmount = 0;
    let totalPriceWithGst = 0;

    if (isSeparateGst) {
      finalSellingPrice = finalMrpPrice;

      gstAmountPerUnit = (finalSellingPrice * gstPercent) / 100;

      totalGstAmount = gstAmountPerUnit * newQty;

      totalPriceWithGst = finalSellingPrice * newQty + totalGstAmount;
    } else {
      finalSellingPrice = finalMrpPrice / (1 + gstPercent / 100);

      gstAmountPerUnit = finalMrpPrice - finalSellingPrice;

      totalGstAmount = gstAmountPerUnit * newQty;

      totalPriceWithGst = finalMrpPrice * newQty;
    }

    const updatedItem = {
      ...existingItem,
      quantity: newQty,
      sizeName,
      sellingPrice: finalSellingPrice,
      gstAmount: totalGstAmount,
      gstPercent,
      totalPrice: totalPriceWithGst,
    };

    dispatch(
      updateOrderedItem({
        index: existingIndex,
        updatedItem,
      })
    );

    setItemQuantities((prev) => ({
      ...prev,
      [itemCode]: newQty,
    }));

    setItemCode(updatedItem.itemCode);
    setItemName(updatedItem.itemName);
    setQuantity(updatedItem.quantity);
    setSelectedSize(updatedItem.sizeName);
    setSellingPrice(updatedItem.sellingPrice);
    setTotalPrice(updatedItem.totalPrice);
  };

  const handleConfirmSwitch = () => {
    let updatedPending = updatePendingOrders(pendingOrders);

    setPendingOrders(updatedPending);

    dispatch(
      setCredentials({
        ...userInfo,
        pendingOrders: updatedPending,
      })
    );

    dispatch(setOrderedItems(pendingToLoad.items));
    setMobileNumber(pendingToLoad.mobileNumber || "");
    setEditingOrderId(pendingToLoad.id);

    setShowSwitchModal(false);
    setPendingToLoad(null);
  };

  // Sets an absolute quantity for a food item card in the left grid
  const applyGridQuantity = (item, newQty) => {
    const cartItem = orderItems.find((oi) => oi.itemCode === item.itemCode);
    const existingIndex = orderItems.findIndex(
      (oi) => oi.itemCode === item.itemCode
    );

    if (newQty <= 0) {
      if (existingIndex !== -1) {
        dispatch(removeOrderedItem(existingIndex));
      }
      setItemQuantities((prev) => {
        const updated = { ...prev };
        delete updated[item.itemCode];
        return updated;
      });
      return;
    }

    // selectedSize is a global that may still hold another item's size name,
    // in which case syncEverywhere would find no matching size and silently
    // skip the update - only use it when it belongs to this item
    const selectedSizeBelongsToItem = item.availableSizes?.some(
      (sz) => sz.sizeName === selectedSize
    );

    const currentSize =
      cartItem?.sizeName ||
      (selectedSizeBelongsToItem ? selectedSize : null) ||
      item.availableSizes?.find((sz) => sz.sizeName?.toLowerCase() === "small")
        ?.sizeName ||
      item.availableSizes?.[0]?.sizeName;

    syncEverywhere(item, newQty, currentSize);
  };

  // Sets an absolute quantity for an already selected item in the order table
  const applyOrderItemQuantity = (item, index, newQty) => {
    if (newQty <= 0) {
      handleDeleteItem(index);
      setItemQuantities((prev) => {
        const updated = { ...prev };
        delete updated[item.itemCode];
        return updated;
      });
      return;
    }

    // syncItemEverywhere checks the stock and warns when it is not enough
    syncItemEverywhere(item.itemCode, newQty);
  };

  const handleDirectAdd = (item, sizeName, qtyChange = 1) => {
    const size =
      item.availableSizes?.find((sz) => sz.sizeName === sizeName) ||
      item.availableSizes?.[0];

    if (!size) return;

    const existingIndex = orderItems.findIndex(
      (oi) => oi.itemCode === item.itemCode
    );

    let quantityNum;

    if (existingIndex !== -1) {
      if (qtyChange === 0) {
        quantityNum = orderItems[existingIndex].quantity;
      } else {
        quantityNum = orderItems[existingIndex].quantity + qtyChange;
      }
    } else {
      quantityNum = qtyChange || 1;
    }
    if (quantityNum <= 0) {
      if (existingIndex !== -1) {
        dispatch(removeOrderedItem(existingIndex));
      }
      return;
    }

    const gstPercent = gstApplicable ? item?.gstTax || 0 : 0;

    const isSeparateGst = userInfo?.outletId.find(
      (ot) => ot._id === userInfo?.selectedOutletId
    )?.separateGst;

    const offer = size.itemPrice || 0;
    const discountPercent = size.discountPercent || 0;
    const discountAmount = size.discountAmount || 0;

    const priceAfterPercent = offer - (offer * discountPercent) / 100;
    const finalMrpPrice = priceAfterPercent - discountAmount;

    let finalSellingPrice = 0;
    let gstAmountPerUnit = 0;
    let totalGstAmount = 0;
    let totalPriceWithGst = 0;

    if (isSeparateGst) {
      finalSellingPrice = finalMrpPrice;
      gstAmountPerUnit = (finalSellingPrice * gstPercent) / 100;
      totalGstAmount = gstAmountPerUnit * quantityNum;
      totalPriceWithGst = finalSellingPrice * quantityNum + totalGstAmount;
    } else {
      finalSellingPrice = finalMrpPrice / (1 + gstPercent / 100);
      gstAmountPerUnit = finalMrpPrice - finalSellingPrice;
      totalGstAmount = gstAmountPerUnit * quantityNum;
      totalPriceWithGst = finalMrpPrice * quantityNum;
    }

    const newItem = {
      _id: item._id,
      itemCode: item.itemCode,
      itemName: item.itemName,
      quantity: quantityNum,
      sizeName: size.sizeName,
      sellingPrice: finalSellingPrice,
      totalPrice: totalPriceWithGst,
      gstAmount: totalGstAmount,
      gstPercent,
    };

    if (existingIndex !== -1) {
      dispatch(
        updateOrderedItem({ index: existingIndex, updatedItem: newItem })
      );
    } else {
      dispatch(addOrderedItem(newItem));
    }
  };

  return (
    <>
      {/* <label>Sales Page</label> */}
      <ReUsableModal
        title={
          editingOrder
            ? `Update existing order ${editingOrder.orderId} ?`
            : "Are you sure to Submit the order"
        }
        size="md"
        payload={[
          {
            fieldName: "Item & Quantity",
            fieldType: "text",
            fieldValue: orderItems
              .map(
                (item) =>
                  `${item.itemName}${
                    item.sizeName && item.sizeName !== "Single Size"
                      ? ` (${item.sizeName})`
                      : ""
                  } - ${item.quantity}`
              )
              .join(", "),
            disabled: true,
          },
          {
            fieldName: "Gross Total",
            fieldType: "text",
            fieldValue: `₹${formatAmount(grossTotal)}`,
            disabled: true,
          },
        ]}
        show={showModal}
        onSubmit={handleSubmit}
        onHide={handleCancel}
        submit="Submit"
        cancel="Cancel"
      />
      {showSwitchModal && (
        <ReUsableModal
          title=" You have an order In-Progress. Do you want to switch ?"
          payload={[]}
          show={showSwitchModal}
          onSubmit={handleConfirmSwitch}
          onHide={() => setShowSwitchModal(false)}
          submit="Yes"
          cancel="No"
        />
      )}

      <Form onKeyDown={handleKeyDown}>
        <Row className="mb-2" style={{ width: "100%" }}>
          <Col>
            <Form.Control
              style={inputBoxStyle}
              ref={itemCodeRef}
              type="text"
              value={itemCode}
              onChange={(e) => {
                setItemCode(e.target.value);
                setItemName("");
                setSellingPrice("");
                setTotalPrice("");
                setSelectedSize(null);
                setAvailableSizes([]);
              }}
              placeholder=" Item Code"
            />
          </Col>
          <Col>
            <Form.Control
              style={inputBoxStyle}
              type="number"
              value={quantity}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "") {
                  setQuantity("");
                  return;
                }

                const qty = Number(value);

                const item =
                  activeOrderItem &&
                  foodItems.find(
                    (fi) => fi.itemCode === activeOrderItem.itemCode
                  );

                // syncEverywhere sets the quantity itself, and leaves it
                // unchanged (with a warning) when stock is not enough
                if (item) syncEverywhere(item, qty, selectedSize);
                else setQuantity(qty);
              }}
              placeholder=" Quantity"
            />
          </Col>
          {availableSizes.length > 1 && (
            <Col>
              <Form.Select
                style={inputBoxStyle}
                value={selectedSize || ""}
                onChange={(e) => {
                  const sizeName = e.target.value;

                  const item =
                    activeOrderItem &&
                    foodItems.find(
                      (fi) => fi.itemCode === activeOrderItem.itemCode
                    );

                  // A bigger size can use more stock per unit, so it is
                  // checked too; syncEverywhere sets the size when allowed
                  if (item) syncEverywhere(item, quantity, sizeName);
                  else setSelectedSize(sizeName);
                }}
              >
                <option value="">Select Size</option>
                {availableSizes.map((size, idx) => (
                  <option key={idx} value={size.sizeName}>
                    {size.sizeName} ({size.sizeDescription || ""})
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}

          <Col>
            {matchingItems.length > 1 ? (
              <Form.Select
                style={inputBoxStyle}
                value={selectedItemId}
                onChange={(e) => setSelectedItemId(e.target.value)}
              >
                <option value="">Select Item</option>
                {matchingItems.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.itemName}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                style={inputBoxStyle}
                type="text"
                value={itemName}
                placeholder="Item Name"
                disabled
              />
            )}
          </Col>
          <Col>
            <Form.Control
              style={inputBoxStyle}
              type="text"
              value={sellingPrice === "" ? "" : formatAmount(sellingPrice)}
              placeholder="Selling Price"
              disabled
            />
          </Col>
          <Col>
            <Form.Control
              style={inputBoxStyle}
              type="text"
              value={totalPrice === "" ? "" : formatAmount(totalPrice)}
              placeholder="Total"
              disabled
            />
          </Col>
          <Col xs="auto">
            <Button className="secondaryButton" onClick={handleAddItem}>
              Add Items
            </Button>
          </Col>
          {availableQty !== 10000 && availableQty !== null && (
            <small style={{ color: "orange" }}>
              {" "}
              Available Quantity : {availableQty}
            </small>
          )}
          {isInvalidCode && (
            <label style={{ color: "orange" }}> Invalid Item Code</label>
          )}
        </Row>
        {/* <Row
          style={{
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            flexDirection: "row",
            paddingBottom: "0.5rem",
          }}
        >
          <Col xs="auto">
            <Button className="" onClick={handleAddItem}>
              Add Items
            </Button>
          </Col>
        </Row> */}
      </Form>

      {/* Order Table and Reference Table side-by-side */}
      <Row style={{ marginBottom: "0px", border: "0.5px solid #d7d2c3" }}>
        <Col
          md={1}
          style={{
            backgroundColor: "#f4f9fd",
            borderRight: "1px solid #e3ecf3",
            padding: "0.4rem 0.35rem",
          }}
        >
          <h6
            style={{
              margin: "0 0 0.5rem 0",
              padding: "0.35rem 0",
              textAlign: "center",
              fontSize: "0.75rem",
              fontWeight: "600",
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              color: "#1d5f91",
              borderBottom: "1px solid #dbe9f4",
            }}
          >
            Categories
          </h6>
          <div
            style={{
              height: "66vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "0.4rem",
            }}
          >
            {getCategories().map((category) => (
              <Button
                key={category}
                style={{
                  width: "100%",
                  padding: "0.45rem 0.2rem",
                  fontSize: "0.78rem",
                  fontWeight: selectedCategory === category ? "700" : "500",
                  borderRadius: "8px",
                  border:
                    selectedCategory === category
                      ? "1px solid #057acd"
                      : "1px solid #dbe4ec",
                  color: selectedCategory === category ? "#ffffff" : "#2c3e50",
                  background:
                    selectedCategory === category
                      ? "linear-gradient(to bottom right, #057acd 0%, #1d5f91 100%)"
                      : "#ffffff",
                  boxShadow:
                    selectedCategory === category
                      ? "0 2px 6px rgba(5, 122, 205, 0.35)"
                      : "0 1px 2px rgba(0, 0, 0, 0.04)",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
                size="sm"
                onClick={() => {
                  setSelectedCategory(category);
                  setItemSearchText("");
                }}
              >
                {category}
              </Button>
            ))}
          </div>
        </Col>
        <Col xs={12} md={5} lg={6}>
          {/* <h6>Categories</h6> */}

          <InputGroup size="sm" style={{ paddingTop: "0.5rem" }}>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search item by name or item code"
              value={itemSearchText}
              onChange={(e) => setItemSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setItemSearchText("");
              }}
            />
            {itemSearchText && (
              <Button
                variant="outline-secondary"
                onClick={() => setItemSearchText("")}
                title="Clear search"
              >
                ✕ Clear
              </Button>
            )}
          </InputGroup>

          {/* Bottom 60vh - Item Buttons */}
          <div
            style={{
              height: "calc(70vh - 2.4rem)",
              overflowY: "auto",
              paddingTop: "0.5rem",
            }}
          >
            {isItemSearchActive && (
              <div
                style={{
                  color: "#c0392b",
                  fontWeight: "600",
                  fontSize: "0.82rem",
                  marginBottom: "0.4rem",
                }}
              >
                You are seeing searched food items ({itemsToShow.length})
                {itemsToShow.length === 0 && (
                  <span style={{ color: "#6c757d", fontWeight: "normal" }}>
                    {" "}
                    - no item matches "{itemSearchText.trim()}"
                  </span>
                )}
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.4rem",
                overflow: "scroll",
              }}
            >
              {itemsToShow?.map((item) => {
                const cartItem = orderItems.find(
                  (oi) => oi.itemCode === item.itemCode
                );

                const quantity = cartItem?.quantity || 0;
                const isAvailable = item.availableQty > 0;
                const isDisabled = item.itemQuantity < 1;

                return (
                  <div
                    className={isDisabled && "disabledColor"}
                    key={item._id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      border:
                        activeOrderItem?.itemCode === item.itemCode
                          ? "2px solid #28a745"
                          : "0.5px solid #ccc",

                      borderRadius: "10px",
                      width: "8.3rem",
                      minHeight: "9rem",
                      padding: "6px",
                      cursor: `${!isDisabled && "pointer"}`,
                    }}
                    onClick={() => {
                      if (isDisabled) return;

                      const defaultSize =
                        item.availableSizes?.find(
                          (sz) => sz.sizeName?.toLowerCase() === "small"
                        )?.sizeName || item.availableSizes?.[0]?.sizeName;

                      setActiveOrderItem({
                        itemCode: item.itemCode,
                        itemName: item.itemName,
                      });

                      setSelectedSize(defaultSize);

                      setItemQuantities((prev) => ({
                        ...prev,
                        [item.itemCode]: prev[item.itemCode] || 0,
                      }));
                    }}
                  >
                    {isItemSearchActive && item.itemCategory && (
                      <span
                        title="Category"
                        style={{
                          alignSelf: "stretch",
                          textAlign: "center",
                          fontSize: "0.68rem",
                          fontWeight: "600",
                          color: "#1d5f91",
                          backgroundColor: "#eef6fc",
                          border: "1px solid #d6eaf8",
                          borderRadius: "6px",
                          padding: "0.05rem 0.3rem",
                          marginBottom: "0.25rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.itemCategory}
                      </span>
                    )}
                    {/* Food Item Button */}
                    <LazyMemberImage
                      foodItemId={item._id}
                      imageUpdatedTimeFromDB={item.imageUploadedTime}
                      currentlySelling={item.currentlySelling}
                      photoUploaded={item.imageUploaded}
                      width="6.5rem"
                      height="5.5rem"
                    />
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        fontSize:
                          item.itemName?.length <= 15 ? "0.9rem" : "0.8rem",
                        textAlign: "center",
                      }}
                      size="sm"
                      disabled={isDisabled}
                      onClick={() => {
                        if (!isDisabled) {
                          setItemCode(item.itemCode);
                          setTimeout(() => itemCodeRef.current?.focus(), 0);
                        }
                      }}
                    >
                      {makeFirstCharCapital(item.itemName)}
                      {!(
                        cartItem || itemQuantities[item.itemCode] !== undefined
                      ) ? (
                        <span style={{ marginTop: "0.3rem", color: "green" }}>
                          Rs.
                          {Number(item?.availableSizes[0]?.discountPercent) >
                            0 ||
                          Number(item?.availableSizes[0]?.discountAmount) >
                            0 ? (
                            <>
                              <small style={{ textDecoration: "line-through" }}>
                                {item?.availableSizes[0]?.itemPrice?.toFixed(2)}
                              </small>
                              <small>
                                {" "}
                                {(
                                  Number(item?.availableSizes[0]?.itemPrice) -
                                  (Number(
                                    item?.availableSizes[0]?.discountPercent
                                  ) *
                                    Number(
                                      item?.availableSizes[0]?.itemPrice
                                    )) /
                                    100 -
                                  Number(
                                    item?.availableSizes[0]?.discountAmount
                                  )
                                ).toFixed(2)}
                              </small>
                            </>
                          ) : (
                            <small>
                              {item?.availableSizes[0]?.itemPrice?.toFixed(2)}
                            </small>
                          )}
                        </span>
                      ) : (
                        <>
                          {/*  SIZE BUTTONS */}
                          {item.availableSizes?.length > 1 && (
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                marginTop: "6px",
                                justifyContent: "center",
                                flexWrap: "nowrap",
                                overflowX: "auto",
                              }}
                            >
                              {item.availableSizes.map((sz) => {
                                const cartItem = orderItems.find(
                                  (oi) => oi.itemCode === item.itemCode
                                );

                                const currentSize =
                                  cartItem?.sizeName || selectedSize;
                                const quantity =
                                  cartItem?.quantity ??
                                  itemQuantities[item.itemCode] ??
                                  0;

                                return (
                                  <button
                                    key={sz.sizeName}
                                    style={{
                                      fontSize: "0.55rem",
                                      minWidth: "40px",
                                      padding: "2px 4px",
                                      textAlign: "center",
                                      borderRadius: "6px",
                                      border: "1px solid #ccc",
                                      background:
                                        currentSize === sz.sizeName
                                          ? "#28a745"
                                          : "#f8f9fa",

                                      color:
                                        currentSize === sz.sizeName
                                          ? "white"
                                          : "black",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      setSelectedSize(sz.sizeName);
                                      setItemCode(item.itemCode);
                                      setItemName(item.itemName);

                                      if (cartItem) {
                                        syncEverywhere(
                                          item,
                                          cartItem.quantity,
                                          sz.sizeName
                                        );
                                      }
                                    }}
                                  >
                                    {sz.sizeName}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {/*  QUANTITY STEPPER - opens 0-1000 dropdown */}
                          <div
                            className="secondaryButton"
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "6px",
                              borderRadius: "10px",
                              padding: "0.25rem 0.6rem",
                              color: "white",
                              fontWeight: "bold",
                              minWidth: "5rem",
                              cursor: "pointer",
                              marginTop: "4px",
                              position: "relative",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isDisabled) return;
                              setQtyDropdownItemCode(item.itemCode);
                            }}
                          >
                            <span style={{ padding: "0 8px" }}>−</span>

                            <span
                              style={{ minWidth: "20px", textAlign: "center" }}
                            >
                              {quantity}
                            </span>

                            <span style={{ padding: "0 8px" }}>+</span>

                            {qtyDropdownItemCode === item.itemCode && (
                              <QuantityDropdown
                                current={
                                  orderItems.find(
                                    (oi) => oi.itemCode === item.itemCode
                                  )?.quantity ||
                                  itemQuantities[item.itemCode] ||
                                  0
                                }
                                onSelect={(newQty) =>
                                  applyGridQuantity(item, newQty)
                                }
                                onClose={() => setQtyDropdownItemCode(null)}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </span>

                    {/* Add Button or Quantity Controls */}
                  </div>
                );
              })}
            </div>
          </div>
        </Col>

        <Col
          xs={12}
          md={5}
          style={{
            border: "0.5px solid #d7d2c3",
          }}
        >
          {pendingOrders.length > 0 && (
            <div style={{ marginBottom: "12px" }}>
              <h6 style={{ color: "#fffff", marginBottom: "6px" }}>
                Pending Orders ({pendingOrders.length})
              </h6>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  maxHeight: "70px",
                  overflowY: "auto",
                }}
                className="hold-scroll"
              >
                {pendingOrders
                  .slice()
                  .reverse()
                  .map((order) => (
                    <div
                      key={order.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        background:
                          order.id === editingOrderId ? "#28a745" : "#8e90f3",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "0.8rem",
                        color: order.id === editingOrderId ? "#fff" : "#ffcc00",
                        gap: "6px",
                        transition: "0.2s",
                        border:
                          order.id === editingOrderId
                            ? "2px solid #fff"
                            : "none",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          order.id === editingOrderId ? "#28a745" : "#2b59f3")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          order.id === editingOrderId ? "#28a745" : "#8e90f3")
                      }
                    >
                      <span
                        style={{
                          cursor: "pointer",
                          fontWeight:
                            order.id === editingOrderId ? "bold" : "normal",
                        }}
                        onClick={() => handleLoadPending(order)}
                      >
                        {order.mobileNumber || `CS-${order.sequence}`}
                        {order.id === editingOrderId && " (Active)"}
                      </span>

                      <span
                        style={{
                          fontWeight: "bold",
                          cursor: "pointer",
                          opacity: 0.8,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingOrders((prev) => {
                            const updated = prev.filter(
                              (o) => o.id !== order.id
                            );

                            setPendingCounter(updated.length + 1);

                            dispatch(
                              setCredentials({
                                ...userInfo,
                                pendingOrders: updated,
                              })
                            );

                            return updated;
                          });
                        }}
                      >
                        ✕
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
          {editingOrder && (
            <div
              style={{
                backgroundColor: "#fdecec",
                border: "1px solid #f5c2c7",
                borderRadius: "10px",
                padding: "0.5rem 0.8rem",
                margin: "0.4rem 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              <span
                style={{
                  color: "#c0392b",
                  fontWeight: "600",
                  fontSize: "0.85rem",
                }}
              >
                You are editing order {editingOrder.orderId} — placing will
                update the existing order
              </span>
              <Button
                variant="outline-danger"
                size="sm"
                style={{ borderRadius: "8px", fontWeight: "600" }}
                onClick={handleCancelEdit}
              >
                ✕ Cancel Edit
              </Button>
            </div>
          )}
          {orderItems?.length > 0 && (
            <div
              style={{
                height: "50vh",
                overflowY: "auto",
                ...(editingOrder
                  ? {
                      backgroundColor: "#fdecec",
                      border: "1px solid #f5c2c7",
                      borderRadius: "10px",
                    }
                  : {}),
              }}
            >
              <Table
                striped
                hover
                responsive
                style={
                  editingOrder
                    ? {
                        "--bs-table-bg": "#fdecec",
                        "--bs-table-striped-bg": "#fbdcdc",
                      }
                    : {}
                }
              >
                <thead>
                  <tr>
                    {/* <th></th> */}
                    <th>Code</th>
                    <th>Item</th>
                    <th>Price</th>
                    <th>Quantity</th>
                    {/* <th>GST</th> */}
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map((item, index) => (
                    <tr key={item.itemCode}>
                      {/* <td>
                        <LazyMemberImage
                          foodItemId={item._id}
                          imageUpdatedTimeFromDB={item.imageUploadedTime}
                          currentlySelling={item.currentlySelling}
                          photoUploaded={item.imageUploaded}
                        />
                      </td> */}
                      <td>{item.itemCode}</td>
                      <td>
                        {/* <LazyMemberImage
                          foodItemId={item._id}
                          imageUpdatedTimeFromDB={item.imageUploadedTime}
                          currentlySelling={item.currentlySelling}
                          photoUploaded={item.imageUploaded}
                          width="3rem"
                          height="3rem"
                        /> */}
                        {item?.itemName}{" "}
                        {item?.sizeName !== "Single Size" &&
                          (editingSizeIndex === index ? (
                            <select
                              value={item.sizeName}
                              onChange={(e) => {
                                syncItemEverywhere(
                                  item.itemCode,
                                  item.quantity,
                                  e.target.value
                                );

                                setEditingSizeIndex(null);
                              }}
                              onBlur={() => setEditingSizeIndex(null)}
                              autoFocus
                            >
                              {foodItems
                                .find((fi) => fi._id === item._id)
                                ?.availableSizes.map((sz, i) => (
                                  <option key={i} value={sz.sizeName}>
                                    {sz.sizeName}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <span
                              style={{ color: "green", cursor: "pointer" }}
                              onClick={() => setEditingSizeIndex(index)}
                            >
                              {item.sizeName}
                            </span>
                          ))}
                      </td>
                      <td>
                        ₹{priceOrderLine(item).sellingPrice.toFixed(2)}
                        {priceOrderLine(item).isCustom && (
                          <small
                            title="Customised price for this customer"
                            style={{
                              color: "#e67e22",
                              fontWeight: "700",
                              marginLeft: "2px",
                            }}
                          >
                            *
                          </small>
                        )}
                      </td>
                      <td>
                        {" "}
                        {editingQtyIndex === index ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              position: "relative",
                            }}
                          >
                            <span>{item.quantity}</span>

                            <QuantityDropdown
                              current={item.quantity}
                              onSelect={(newQty) =>
                                applyOrderItemQuantity(item, index, newQty)
                              }
                              onClose={() => setEditingQtyIndex(null)}
                            />
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <span>{item.quantity}</span>

                            <span
                              style={{ cursor: "pointer", color: "#007bff" }}
                              onClick={() => setEditingQtyIndex(index)}
                            >
                              <GrEdit />
                            </span>
                          </div>
                        )}
                      </td>
                      {/* <td>
                        <span>{Number(item?.gstAmount).toFixed(2)}</span>
                        <small style={{ fontSize: "0.6rem" }}>
                          {item?.gstPercent}%
                        </small>
                      </td> */}
                      <td>
                        ₹{formatAmount(priceOrderLine(item).lineTotal)}
                      </td>
                      <td>
                        <MdDeleteForever
                          style={{ cursor: "pointer" }}
                          onClick={() => handleDeleteItem(index)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <hr />
          <Row
            className="justify-content-center"
            style={{ marginTop: "0.2rem" }}
          >
            {" "}
            {orderItems?.length === 0 && newOrders?.[0] && (
              <>
                {" "}
                <label
                  style={{
                    color: "green",
                    textAlign: "center",
                    textDecoration: "underline",
                  }}
                >
                  Your Last Order{" "}
                </label>
                <Row
                  style={{ margin: "0.5rem auto", justifyContent: "center" }}
                >
                  <Col xs="auto" style={{ padding: 0 }}>
                    <OrderCard order={newOrders[0]} />
                  </Col>
                </Row>
              </>
            )}
          </Row>
          <Row
            className=" justify-content-center"
            style={{
              marginTop:
                orderItems?.length === 0 && !newOrders?.[0] ? "15rem" : "",
            }}
          >
            <Col xs="auto">
              <h5 style={{ color: "orange", margin: "0.02rem" }}>
                <strong style={{ color: "green" }}>
                  {" "}
                  Total : ₹ {totalOrderAmount?.toFixed(2)}
                </strong>{" "}
                <span>| total items : {orderItems?.length}</span>
              </h5>
            </Col>
          </Row>
          {/* Sticky action bar - pinned to the bottom of the screen so it
              stays visible and usable however long the order list grows */}
          <div
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 10,
              backgroundColor: "#ffffff",
              borderTop: "1px solid #e0e0e0",
              borderRadius: "12px 12px 0 0",
              boxShadow: "0 -4px 10px rgba(0, 0, 0, 0.08)",
              padding: "0.6rem 0.5rem 0.8rem",
              marginTop: "0.3rem",
            }}
          >
            {orderItems?.length > 0 && (
              <div
                className="d-flex justify-content-center align-items-start flex-wrap"
                style={{ gap: "0.6rem", marginBottom: "0.6rem" }}
              >
                <div style={{ width: "11rem" }}>
                  <Form.Control
                    type="tel"
                    placeholder="customer mobile"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    maxLength={10}
                    pattern="[0-9]*"
                    style={{
                      fontSize: "0.9rem",
                      borderColor: mobileRequiredError ? "#dc3545" : undefined,
                    }}
                  />
                  {mobileRequiredError && (
                    <small
                      style={{
                        color: "#dc3545",
                        fontSize: "0.72rem",
                        display: "block",
                        marginTop: "0.15rem",
                      }}
                    >
                      Mobile required when amount not collected
                    </small>
                  )}
                </div>
                <div style={{ position: "relative", width: "13rem" }}>
                  <Form.Control
                    type="text"
                    placeholder="Search customer name / mobile"
                    value={customerSearchText}
                    onChange={(e) => setCustomerSearchText(e.target.value)}
                    style={{ fontSize: "0.9rem" }}
                  />
                  {customerSuggestions.length > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "100%",
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "8px 8px 0 0",
                        maxHeight: "220px",
                        overflowY: "auto",
                        boxShadow: "0 -4px 8px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      {customerSuggestions.map((customer) => (
                        <div
                          key={customer._id}
                          onClick={() => handleSelectCustomer(customer)}
                          style={{
                            padding: "0.4rem 0.6rem",
                            cursor: "pointer",
                            borderBottom: "1px solid #eee",
                            fontSize: "0.85rem",
                          }}
                        >
                          <strong>{customer.customerName}</strong>{" "}
                          {customer.postPaidCustomer && (
                            <Badge bg="success" pill>
                              Postpaid
                            </Badge>
                          )}
                          <br />
                          <small className="text-muted">
                            {customerMobileOf(customer)}
                          </small>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {selectedCustomer && (
                  <div style={{ marginTop: "0.4rem", fontSize: "0.85rem" }}>
                    <strong>{selectedCustomer.customerName}</strong>
                    <span
                      onClick={() => {
                        setSelectedCustomer(null);
                        setMobileNumber("");
                        setAmountPaid(false);
                      }}
                      style={{
                        color: "red",
                        cursor: "pointer",
                        marginLeft: "0.5rem",
                        fontWeight: "bold",
                      }}
                      title="Clear selected customer"
                    >
                      ✕
                    </span>
                  </div>
                )}
              </div>
            )}

            <div
              className="d-flex justify-content-center flex-wrap"
              style={{ gap: "0.6rem", marginBottom: "0.6rem" }}
            >
              <Button
                className="secondaryButton"
                style={{
                  border: "0.2px solid rgb(4, 167, 182)",
                  width: "11rem",
                  ...(editingOrder
                    ? { backgroundColor: "#c0392b", borderColor: "#c0392b" }
                    : {}),
                }}
                variant="primary"
                onClick={handleConfirmOrder}
                disabled={!orderItems?.length > 0 || editOrderLoading}
              >
                {editingOrder
                  ? editOrderLoading
                    ? "Updating..."
                    : "Update Order"
                  : "Place Order"}
              </Button>
              <Button
                variant="warning"
                style={{ width: "13rem" }}
                disabled={orderItems.length === 0 || Boolean(editingOrder)}
                title={
                  editingOrder
                    ? "Not available while editing an existing order"
                    : undefined
                }
                onClick={handleMoveToPending}
              >
                Move to Pending
              </Button>
            </div>

            <div
              className="d-flex justify-content-center align-items-center flex-wrap"
              style={{ gap: "1rem" }}
            >
              {isWholeSaleOutlet && (
                <div
                  className="d-flex align-items-center"
                  style={{ gap: "0.4rem" }}
                >
                  <Form.Label
                    className="mb-0"
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Invoice Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    style={{ width: "10.5rem", fontSize: "0.9rem" }}
                  />
                </div>
              )}
              <Form.Check
                type="checkbox"
                label={"Delivered ?"}
                checked={orderDelivered}
                onChange={() => setOrderDelivered(!orderDelivered)}
              />
              <Form.Check
                type="checkbox"
                label={"Amount Collected ?"}
                checked={isAmountCollected}
                onChange={() => setIsAmountCollected(!isAmountCollected)}
              />
              <div style={{ width: "12rem" }}>
                <Form.Select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  style={{
                    fontSize: "0.9rem",
                    fontWeight:
                      paymentMode === "Payment Mode" ? "normal" : "600",
                    color:
                      paymentMode === "Payment Mode" ? "#6c757d" : "#1d5f91",
                    cursor: "pointer",
                    borderColor: paymentModeError ? "#dc3545" : undefined,
                  }}
                >
                  <option value="Payment Mode" disabled>
                    Payment Mode
                  </option>
                  {[
                    "Cash",
                    "UPI",
                    "Debit Card",
                    "Credit Card",
                    "Net Banking",
                    "Gpay",
                    "PhonePe",
                    "PayTM",
                    "Amazon Pay",
                    "Wallet",
                    "Check",
                    "Demand Draft",
                    "Others",
                  ].map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </Form.Select>
                {paymentModeError && (
                  <small
                    style={{
                      color: "#dc3545",
                      fontSize: "0.72rem",
                      display: "block",
                      marginTop: "0.15rem",
                    }}
                  >
                    Payment mode required when amount collected
                  </small>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

const inputBoxStyle = {
  fontSize: "1rem",
};

export default NewSalesScreen;
